import { useFrame, useThree } from '@react-three/fiber';
import { useRef } from 'react';
import { AdditiveBlending, Vector3, type Mesh } from 'three';
import { prefersReducedMotion } from '../styles/tokens.js';

/**
 * Meteors.
 *
 * Three of them, each waiting out its own stretch of sky on its own clock. One
 * shared timer gives an even drumbeat, which reads as a machine; three
 * independent ones bunch up and leave gaps the way a real shower does, and
 * occasionally two cross at once. Roughly one every ten seconds between them,
 * but you can never quite tell when.
 */
const METEORS = 3;
const MIN_WAIT = 12;
const MAX_WAIT = 48;
const MIN_FLIGHT = 0.6;
const MAX_FLIGHT = 1.9;

interface Flight {
  from: Vector3;
  to: Vector3;
  /** Seconds from first spark to burnout. */
  duration: number;
  /** How fat and bright this one is. Most are modest; a few are not. */
  weight: number;
}

function randomFlight(): Flight {
  // Near the viewer, between the camera and the system, rather than out past
  // the orbits — a meteor burns up a few miles overhead, and one placed behind
  // the planets is both wrong and far too small to see.
  const side = Math.random() < 0.5 ? -1 : 1;
  const from = new Vector3(
    side * (4 + Math.random() * 5),
    1.5 + Math.random() * 4,
    1.5 + Math.random() * 3,
  );
  const travel = 5 + Math.random() * 7;
  const to = from.clone().add(
    new Vector3(-side * travel, -1.5 - Math.random() * 3.5, Math.random() * 2 - 0.5),
  );
  return {
    from,
    to,
    duration: MIN_FLIGHT + Math.random() * (MAX_FLIGHT - MIN_FLIGHT),
    // A steep curve: the big bright ones stay rare.
    weight: 0.6 + Math.pow(Math.random(), 3) * 1.6,
  };
}

export function ShootingStars() {
  const still = prefersReducedMotion();
  if (still) return null;
  return (
    <group>
      {Array.from({ length: METEORS }, (_, i) => (
        <Meteor key={i} />
      ))}
    </group>
  );
}

function Meteor() {
  const { invalidate } = useThree();
  const head = useRef<Mesh>(null);
  const tail = useRef<Mesh>(null);
  // Each starts somewhere different in its own cycle, so they never march.
  const state = useRef({
    // Not the full wait, so the sky has something in it soon after you arrive.
    next: Math.random() * 18,
    elapsed: 0,
    flying: false,
    flight: randomFlight(),
  });

  useFrame((_, delta) => {
    const s = state.current;

    if (!s.flying) {
      s.next -= delta;
      if (s.next > 0) return;
      s.flying = true;
      s.elapsed = 0;
      s.flight = randomFlight();
    }

    // Read after the new flight is chosen: taking it before meant the first
    // frame of every meteor was drawn along the path of the last one.
    const { from, to, weight } = s.flight;

    s.elapsed += delta;
    const t = s.elapsed / s.flight.duration;

    if (t >= 1) {
      s.flying = false;
      s.next = MIN_WAIT + Math.random() * (MAX_WAIT - MIN_WAIT);
      if (head.current) head.current.visible = false;
      if (tail.current) tail.current.visible = false;
      invalidate();
      return;
    }

    const position = from.clone().lerp(to, t);
    // Fades in fast and out slowly, which is how one actually looks.
    const fade = Math.min(1, t * 6) * (1 - t) ** 1.5;

    if (head.current) {
      head.current.visible = true;
      head.current.position.copy(position);
      head.current.scale.setScalar((0.012 + fade * 0.022) * weight);
      const material = head.current.material as { opacity: number };
      material.opacity = fade;
    }
    if (tail.current) {
      const behind = from.clone().lerp(to, Math.max(0, t - 0.035 - weight * 0.02));
      tail.current.visible = true;
      tail.current.position.copy(behind.clone().add(position).multiplyScalar(0.5));
      // A cylinder stands along its own y axis, so it is laid down first and
      // then aimed: without this the streak points at the sky, not along it.
      tail.current.lookAt(position);
      tail.current.rotateX(Math.PI / 2);
      tail.current.scale.set(weight, behind.distanceTo(position) || 0.001, weight);
      const material = tail.current.material as { opacity: number };
      material.opacity = fade * 0.5 * Math.min(1, weight);
    }
    invalidate();
  });

  return (
    <group>
      <mesh ref={head} visible={false}>
        <sphereGeometry args={[1, 8, 8]} />
        <meshBasicMaterial color="#ffffff" transparent opacity={0} blending={AdditiveBlending} depthWrite={false} />
      </mesh>
      <mesh ref={tail} visible={false}>
        <cylinderGeometry args={[0.002, 0.012, 1, 6]} />
        <meshBasicMaterial color="#cfe0ff" transparent opacity={0} blending={AdditiveBlending} depthWrite={false} />
      </mesh>
    </group>
  );
}
